/**
 * PATH: packages/email-hub/src/feature/components/rich-text/Toolbar.tsx
 * FILE_ID: EMAILHUB-RICHTEXT-TOOLBAR-001
 * PURPOSE: Formatting toolbar bound to the TipTap editor — fonts, marks,
 *          colour/highlight, headings/quote/lists, alignment, link, image,
 *          and a contextual table-control row (rows/cols/merge/colour/formula).
 */

import { useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react'
import { useCloseOnOutsidePointer } from '@quotech/ui'
import { createPortal } from 'react-dom'
import type { Editor } from '@tiptap/core'
import {
  AlignCenter, AlignLeft, AlignRight, Bold, Highlighter, Image as ImageIcon,
  IndentDecrease, IndentIncrease, Italic, Link as LinkIcon, List, ListOrdered,
  Heading2, Palette, Quote, Redo2, RemoveFormatting, Strikethrough,
  Underline as UnderlineIcon, Undo2, Sigma,
  Table as TableIcon, Columns3, Rows3, Trash2, PaintBucket, Sparkles,
} from 'lucide-react'
import { promptDialog } from '@quotech/ui'
import { promptInsertLink } from './linkPrompt'

const TEXT_COLORS = ['#111827', '#475569', '#dc2626', '#ea580c', '#d97706', '#059669', '#0284c7', '#4f46e5', '#7c3aed', '#db2777']
const HIGHLIGHTS = ['#fef08a', '#bbf7d0', '#bfdbfe', '#fbcfe8', '#fed7aa', '#ddd6fe', '#e5e7eb']
const CELL_COLORS = ['#fee2e2', '#fef9c3', '#dcfce7', '#dbeafe', '#f3e8ff', '#f3f4f6', '#ffffff']
const FONTS = [
  { label: 'Default', value: '' },
  { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
  { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
  { label: 'Times', value: '"Times New Roman", Times, serif' },
  { label: 'Courier', value: '"Courier New", Courier, monospace' },
  { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
]
const SIZES = [
  { label: 'Small', value: '12px' },
  { label: 'Normal', value: '' },
  { label: 'Medium', value: '16px' },
  { label: 'Large', value: '20px' },
  { label: 'X-Large', value: '24px' },
  { label: 'Huge', value: '32px' },
]

function useEditorRevision(editor: Editor | null) {
  const [, force] = useReducer((x: number) => x + 1, 0)
  useEffect(() => {
    if (!editor) return
    const h = () => force()
    editor.on('selectionUpdate', h)
    editor.on('transaction', h)
    return () => { editor.off('selectionUpdate', h); editor.off('transaction', h) }
  }, [editor])
}

export function Toolbar({ editor, onInsertImage, onAskAi }: { editor: Editor | null; onInsertImage: () => void; onAskAi?: () => void }) {
  useEditorRevision(editor)
  const [colorOpen, setColorOpen] = useState(false)
  const [hlOpen, setHlOpen] = useState(false)
  const [cellOpen, setCellOpen] = useState(false)
  if (!editor) return null

  const inTable = editor.isActive('table')
  const inList = editor.isActive('bulletList') || editor.isActive('orderedList')
  const canIndent = editor.can().sinkListItem('listItem')
  const canOutdent = editor.can().liftListItem('listItem')

  const handleLink = () => promptInsertLink(editor)

  const handleFormula = async () => {
    const cur = (editor.getAttributes('tableCell').formula || editor.getAttributes('tableHeader').formula || '') as string
    const formula = await promptDialog({
      title: 'Cell formula',
      description: 'e.g. =SUM(A1:A3), =AVG(B1:B4), =A1*B1. Cells are A1, B2 … by column letter and row number.',
      inputLabel: 'Formula', placeholder: '=SUM(A1:A3)', defaultValue: cur, submitLabel: 'Apply',
    })
    if (formula === null) return
    editor.chain().focus().setCellAttribute('formula', formula.trim() || null).run()
  }

  return (
    <div className="qte-toolbar flex flex-wrap items-center gap-0.5 mb-2 pb-2 border-b border-border">
      <select aria-label="Font" title="Font" defaultValue="" onChange={(e) => editor.chain().focus().setFontFamily(e.target.value).run()}
        className="h-7 max-w-[96px] rounded border border-border bg-background px-1 text-xs text-foreground">
        {FONTS.map((f) => <option key={f.label} value={f.value}>{f.label}</option>)}
      </select>
      <select aria-label="Font size" title="Font size" defaultValue="" onChange={(e) => { const v = e.target.value; if (v) editor.chain().focus().setFontSize(v).run(); else editor.chain().focus().unsetFontSize().run() }}
        className="h-7 rounded border border-border bg-background px-1 text-xs text-foreground">
        {SIZES.map((s) => <option key={s.label} value={s.value}>{s.label}</option>)}
      </select>
      <Sep />
      <Btn icon={Bold} title="Bold (⌘B)" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()} />
      <Btn icon={Italic} title="Italic (⌘I)" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()} />
      <Btn icon={UnderlineIcon} title="Underline (⌘U)" active={editor.isActive('underline')} onClick={() => editor.chain().focus().toggleUnderline().run()} />
      <Btn icon={Strikethrough} title="Strikethrough" active={editor.isActive('strike')} onClick={() => editor.chain().focus().toggleStrike().run()} />
      <Sep />
      <Pop open={colorOpen} setOpen={setColorOpen} icon={Palette} title="Text colour">
        <Swatches colors={TEXT_COLORS} onPick={(c) => { editor.chain().focus().setColor(c).run(); setColorOpen(false) }} onClear={() => { editor.chain().focus().unsetColor().run(); setColorOpen(false) }} />
      </Pop>
      <Pop open={hlOpen} setOpen={setHlOpen} icon={Highlighter} title="Highlight">
        <Swatches colors={HIGHLIGHTS} onPick={(c) => { editor.chain().focus().toggleHighlight({ color: c }).run(); setHlOpen(false) }} onClear={() => { editor.chain().focus().unsetHighlight().run(); setHlOpen(false) }} />
      </Pop>
      <Sep />
      <Btn icon={Heading2} title="Heading" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} />
      <Btn icon={Quote} title="Block quote" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()} />
      <Btn icon={List} title="Bullet list" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()} />
      <Btn icon={ListOrdered} title="Numbered list (1, 2, 3)" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()} />
      <Btn icon={IndentDecrease} title={inList ? 'Outdent - promote item (⇧Tab)' : 'Outdent (use inside a list)'} disabled={!canOutdent} onClick={() => editor.chain().focus().liftListItem('listItem').run()} />
      <Btn icon={IndentIncrease} title={inList ? 'Indent - make a sub-point (Tab)' : 'Indent (use inside a list)'} disabled={!canIndent} onClick={() => editor.chain().focus().sinkListItem('listItem').run()} />
      <Sep />
      <Btn icon={AlignLeft} title="Align left" active={editor.isActive({ textAlign: 'left' })} onClick={() => editor.chain().focus().setTextAlign('left').run()} />
      <Btn icon={AlignCenter} title="Align centre" active={editor.isActive({ textAlign: 'center' })} onClick={() => editor.chain().focus().setTextAlign('center').run()} />
      <Btn icon={AlignRight} title="Align right" active={editor.isActive({ textAlign: 'right' })} onClick={() => editor.chain().focus().setTextAlign('right').run()} />
      <Sep />
      <Btn icon={LinkIcon} title="Insert link" active={editor.isActive('link')} onClick={handleLink} />
      <Btn icon={ImageIcon} title="Insert image" onClick={onInsertImage} />
      <Btn icon={TableIcon} title="Insert 3×3 table" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} />
      {onAskAi && (
        <button
          type="button"
          title="Ask AI (fill a table, draft a formatted email…)"
          onMouseDown={(e) => { e.preventDefault(); onAskAi() }}
          className="flex items-center gap-1 px-2 py-1.5 rounded text-violet-600 dark:text-violet-300 hover:bg-violet-500/10 transition-colors"
        >
          <Sparkles className="h-4 w-4" />
          <span className="text-xs font-medium">Ask AI</span>
        </button>
      )}
      <Sep />
      <Btn icon={RemoveFormatting} title="Clear formatting" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} />
      <Btn icon={Undo2} title="Undo (⌘Z)" onClick={() => editor.chain().focus().undo().run()} />
      <Btn icon={Redo2} title="Redo (⌘⇧Z)" onClick={() => editor.chain().focus().redo().run()} />

      {inTable && (
        <div className="mt-1 flex w-full flex-wrap items-center gap-0.5 border-t border-border pt-1">
          <span className="px-1 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">Table</span>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().addRowBefore().run()} className="px-1.5 py-1 rounded text-xs text-muted-foreground hover:bg-accent hover:text-foreground" title="Add row above">+ Row above</button>
          <Btn icon={Rows3} title="Add row below" onClick={() => editor.chain().focus().addRowAfter().run()} />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().addColumnBefore().run()} className="px-1.5 py-1 rounded text-xs text-muted-foreground hover:bg-accent hover:text-foreground" title="Add column left">+ Col left</button>
          <Btn icon={Columns3} title="Add column right" onClick={() => editor.chain().focus().addColumnAfter().run()} />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().deleteRow().run()} className="px-1.5 py-1 rounded text-xs text-muted-foreground hover:bg-accent hover:text-foreground" title="Delete row">- Row</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().deleteColumn().run()} className="px-1.5 py-1 rounded text-xs text-muted-foreground hover:bg-accent hover:text-foreground" title="Delete column">- Col</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().mergeOrSplit().run()} className="px-1.5 py-1 rounded text-xs text-muted-foreground hover:bg-accent hover:text-foreground" title="Merge / split cells">Merge</button>
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().toggleHeaderRow().run()} className="px-1.5 py-1 rounded text-xs text-muted-foreground hover:bg-accent hover:text-foreground" title="Toggle header row">Header</button>
          <Pop open={cellOpen} setOpen={setCellOpen} icon={PaintBucket} title="Cell colour">
            <Swatches colors={CELL_COLORS} onPick={(c) => { editor.chain().focus().setCellAttribute('backgroundColor', c).run(); setCellOpen(false) }} onClear={() => { editor.chain().focus().setCellAttribute('backgroundColor', null).run(); setCellOpen(false) }} />
          </Pop>
          <Btn icon={Sigma} title="Cell formula (=SUM, =AVG …)" onClick={handleFormula} />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => editor.chain().focus().deleteTable().run()} className="ml-auto flex items-center gap-1 px-1.5 py-1 rounded text-xs text-destructive hover:bg-destructive/10" title="Delete table"><Trash2 className="h-3.5 w-3.5" /></button>
        </div>
      )}
    </div>
  )
}

function Btn({ icon: Icon, title, onClick, active = false, disabled = false }: { icon: React.ComponentType<{ className?: string }>; title: string; onClick: () => void; active?: boolean; disabled?: boolean }) {
  return (
    <button type="button" aria-pressed={active} title={title} disabled={disabled}
      onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick() }}
      className={'p-1.5 rounded transition-colors ' + (disabled ? 'text-muted-foreground/40 cursor-not-allowed' : active ? 'bg-primary/15 text-primary' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
      <Icon className="h-4 w-4" />
    </button>
  )
}

function Sep() { return <div className="w-px h-5 bg-border mx-1" aria-hidden /> }

/** Swatch popover. Rendered through a PORTAL with fixed coordinates because
 *  the editor body is an `overflow-y-auto` scroll box (which makes overflow-x
 *  compute to `auto` too) — an in-flow `absolute` popover was being CLIPPED on
 *  the right edge (operator 2026-06-29: "palette not shown correctly"). Fixed
 *  positioning escapes every ancestor's clip; we clamp it into the viewport. */
function Pop({ open, setOpen, icon: Icon, title, children }: { open: boolean; setOpen: (v: boolean) => void; icon: React.ComponentType<{ className?: string }>; title: string; children: React.ReactNode }) {
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [coords, setCoords] = useState<{ left: number; top: number } | null>(null)

  // Anchor the popover under the trigger, clamped inside the viewport so it
  // never gets cut off when the button sits near the right/bottom edge.
  useLayoutEffect(() => {
    if (!open || !btnRef.current) { setCoords(null); return }
    const r = btnRef.current.getBoundingClientRect()
    const POP_W = 168
    const left = Math.max(8, Math.min(r.left, window.innerWidth - POP_W - 8))
    setCoords({ left, top: r.bottom + 4 })
  }, [open])

  // Close on outside click / Escape (a scroll or resize would drift the anchor,
  // so close then too — simpler + safer than live-repositioning).
  // Reused from: packages/ui/src/hooks/useCloseOnOutsidePointer.ts (useCloseOnOutsidePointer)
  useCloseOnOutsidePointer(open, () => setOpen(false), [btnRef, popRef])
  useEffect(() => {
    if (!open) return
    const onScrollOrResize = () => setOpen(false)
    window.addEventListener('scroll', onScrollOrResize, true)
    window.addEventListener('resize', onScrollOrResize)
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true)
      window.removeEventListener('resize', onScrollOrResize)
    }
  }, [open, setOpen])

  return (
    <>
      <button ref={btnRef} type="button" title={title} aria-haspopup="true" aria-expanded={open}
        onMouseDown={(e) => { e.preventDefault(); setOpen(!open) }}
        className="p-1.5 rounded text-muted-foreground hover:bg-accent hover:text-foreground"><Icon className="h-4 w-4" /></button>
      {open && coords && createPortal(
        <div ref={popRef} role="menu" aria-label={title}
          className="fixed z-[95] rounded-md border border-border bg-popover p-2 shadow-lg"
          style={{ left: coords.left, top: coords.top }}>
          {children}
        </div>,
        document.body,
      )}
    </>
  )
}

function Swatches({ colors, onPick, onClear }: { colors: string[]; onPick: (c: string) => void; onClear: () => void }) {
  return (
    <div className="grid grid-cols-4 gap-1.5">
      <button type="button" title="Clear" onMouseDown={(e) => e.preventDefault()} onClick={onClear}
        className="flex h-7 w-7 items-center justify-center rounded border border-border text-xs text-muted-foreground">×</button>
      {colors.map((c) => (
        <button key={c} type="button" title={c} onMouseDown={(e) => e.preventDefault()} onClick={() => onPick(c)}
          className="h-7 w-7 rounded border border-border" style={{ background: c }} />
      ))}
    </div>
  )
}
